import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { createInsight, type InsightCreatePayload, type Insight } from '@/services/DataService';

/**
 * Hook to create a new insight via API
 *
 * This hook handles the creation of insights (scheduled reports) and automatically
 * invalidates the insights query cache to refresh the reports list in the UI.
 *
 * @param options Optional mutation options to customize behavior (onSuccess, onError, etc.)
 * @returns Mutation for creating an insight
 *
 * @example
 * const createMutation = useCreateInsight({
 *   onSuccess: () => {
 *     toast({ title: 'Report scheduled successfully' });
 *   },
 *   onError: (error) => {
 *     toast({ title: 'Failed to schedule report', variant: 'destructive' });
 *   }
 * });
 *
 * createMutation.mutate({
 *   insight: {
 *     source: 'CHAT',
 *     workflow_execution_id: 'abc-123',
 *     display_name: 'My Report',
 *     blocks: [...],
 *     schedule: { cron: '0 9 * * 1', timezone: 'America/New_York' },
 *     data_range: { type: 'relative', unit: 'DAYS', value: 30 }
 *   }
 * });
 */
export const useCreateInsight = (
  options?: Omit<UseMutationOptions<Insight, Error, InsightCreatePayload>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createInsight,
    onSuccess: (data, variables, context) => {
      // Invalidate insights query to refetch the list and show the new pending report
      queryClient.invalidateQueries({ queryKey: ['insights'] });

      // Call custom onSuccess if provided
      options?.onSuccess?.(data, variables, context);
    },
  });
};
